import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, Search, X, ShieldCheck } from "lucide-react";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { label: "Events", to: "/events" },
  { label: "Petitions", to: "/petitions" },
  { label: "Discussions", to: "/discussions" },
  { label: "Blogs", to: "/blogs" },
  { label: "Spotlight", to: "/spotlight" },
  { label: "Fellowship", to: "/fellowship" },
];

const Navbar = () => {
  const { user, login, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const search = (e: React.FormEvent) => {
    e.preventDefault();
    if (q.trim()) navigate(`/events?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header className="sticky top-0 z-50 bg-black/70 backdrop-blur-2xl border-b border-white/10" data-testid="navbar">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 h-[70px] flex items-center gap-6">
        <Link to="/" className="flex items-center gap-3 shrink-0 group" data-testid="nav-logo">
          <span className="transition-transform duration-500 group-hover:rotate-90 inline-block">
            <Logo size={32} />
          </span>
          <span className="font-bebas text-2xl tracking-[0.14em] leading-none">
            PEOPLE<span className="text-neon">'</span>S CIRCLE
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 ml-4">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={`px-3 py-2 text-[13px] font-semibold uppercase tracking-widest transition-colors duration-200 ${
                pathname.startsWith(n.to) ? "text-neon" : "text-white/70 hover:text-white"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <form onSubmit={search} className="hidden xl:flex items-center gap-2 ml-auto border border-white/10 px-3 h-9 bg-white/[0.03] focus-within:border-neon/60 transition-colors">
          <Search size={15} className="text-white/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search events, petitions"
            data-testid="global-search-input"
            className="bg-transparent outline-none text-sm w-52 placeholder:text-white/30"
          />
        </form>

        <div className="ml-auto xl:ml-0 flex items-center gap-3">
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin" className="hidden sm:flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-widest text-pcyellow hover:text-white transition-colors">
                  <ShieldCheck size={15} /> Admin
                </Link>
              )}
              <Link to="/dashboard" data-testid="nav-dashboard" className="flex items-center gap-2 group">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full border border-white/20 group-hover:border-neon transition-colors" />
                ) : (
                  <span className="w-8 h-8 grid place-items-center bg-neon text-xs font-bold">{user.name[0]}</span>
                )}
                <span className="hidden md:block text-sm font-semibold">{user.name.split(" ")[0]}</span>
              </Link>
              <button onClick={logout} data-testid="nav-logout" className="hidden sm:block text-[12px] uppercase tracking-widest text-white/50 hover:text-neon transition-colors">
                Logout
              </button>
            </>
          ) : (
            <button
              onClick={login}
              data-testid="nav-login-btn"
              className="bg-neon px-5 h-10 whitespace-nowrap text-[12px] font-bold uppercase tracking-[0.18em] hover:pc-glow hover:brightness-110 transition-all duration-200"
            >
              Join the circle
            </button>
          )}
          <button className="lg:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/10 bg-black/95 px-5 py-4 grid gap-1" data-testid="nav-mobile-menu">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="py-2 text-sm font-semibold uppercase tracking-widest text-white/80">
              {n.label}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
};

export default Navbar;
